import React from 'react';
import CodeExample from './CodeExample';
import ParameterTable from './ParameterTable';
import ResponseExample from './ResponseExample';
import ApiKeyTip from './ApiKeyTip';

const EndpointCard = ({ endpoint, isDarkMode = false }) => {
  if (!endpoint) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <svg className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Select an endpoint from the sidebar to view documentation</p>
        </div>
      </div>
    );
  }

  const processedEndpoint = {
    ...endpoint,
    endpoint: endpoint.endpoint.replace(/{([^}]+)}/g, (match, param) => {
      const pathParam = endpoint.parameters?.find(p => p.name === param && p.in === 'path');
      return pathParam?.default || `YOUR_${param.toUpperCase()}`;
    })
  };

  // Check if endpoint requires API key authentication
  const requiresApiKey = endpoint.headers?.some(header => 
    header.name.toLowerCase() === 'x-api-key' && header.required
  );

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Show API Key Tip for endpoints that require authentication */}
      {requiresApiKey && <ApiKeyTip isDarkMode={isDarkMode} />}
      
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <span className={`inline-block px-3 py-1 text-sm font-semibold rounded-md mr-3 ${
            endpoint.method === 'GET' 
              ? isDarkMode ? 'bg-emerald-600 text-emerald-100' : 'bg-green-100 text-green-700'
              : endpoint.method === 'POST'
              ? isDarkMode ? 'bg-blue-600 text-blue-100' : 'bg-blue-100 text-blue-700'
              : endpoint.method === 'PUT'
              ? isDarkMode ? 'bg-amber-600 text-amber-100' : 'bg-yellow-100 text-yellow-700'
              : endpoint.method === 'DELETE'
              ? isDarkMode ? 'bg-red-600 text-red-100' : 'bg-red-100 text-red-700'
              : isDarkMode ? 'bg-slate-600 text-slate-100' : 'bg-gray-100 text-gray-700'
          }`}>
            {endpoint.method}
          </span>
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{endpoint.name}</h1>
        </div>
        
        <p className={`mb-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>{endpoint.description}</p>
        
        <div className={`rounded-lg px-4 py-2 font-mono text-sm ${
          isDarkMode 
            ? 'bg-slate-800 text-slate-300 border border-slate-700' 
            : 'bg-gray-100 text-gray-700'
        }`}>
          https://client-api.fluxuro.ai{processedEndpoint.endpoint}
        </div>
      </div>

      {/* Headers */}
      {endpoint.headers && endpoint.headers.length > 0 && (
        <div className="mb-8">
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Headers</h2>
          <div className={`rounded-lg border overflow-hidden ${
            isDarkMode 
              ? 'bg-slate-800 border-slate-700' 
              : 'bg-white border-gray-200'
          }`}>
            <table className={`min-w-full divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-200'}`}>
              <thead className={isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? 'text-slate-300' : 'text-gray-500'
                  }`}>Name</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? 'text-slate-300' : 'text-gray-500'
                  }`}>Value</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? 'text-slate-300' : 'text-gray-500'
                  }`}>Required</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? 'text-slate-300' : 'text-gray-500'
                  }`}>Description</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${
                isDarkMode 
                  ? 'bg-slate-800 divide-slate-700' 
                  : 'bg-white divide-gray-200'
              }`}>
                {endpoint.headers.map((header, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className={`text-sm font-mono ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>{header.name}</code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className={`text-sm font-mono ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>{header.value}</code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {header.required && (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          isDarkMode 
                            ? 'bg-red-600 text-red-100' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          Required
                        </span>
                      )}
                    </td>
                    <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>{header.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Parameters */}
      {endpoint.parameters && endpoint.parameters.length > 0 && (
        <ParameterTable parameters={endpoint.parameters} isDarkMode={isDarkMode} />
      )}

      {/* Request Body */}
      {endpoint.body && (
        <div className="mb-8">
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Request Body</h2>
          <div className={`rounded-lg p-4 overflow-x-auto ${
            isDarkMode 
              ? 'bg-slate-800 border border-slate-700' 
              : 'bg-gray-900'
          }`}>
            <pre className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-300'}`}>
              <code>{JSON.stringify(endpoint.body, null, 2)}</code>
            </pre>
          </div>
        </div>
      )}

      {/* Code Examples */}
      <div className="mb-8">
        <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Code Examples</h2>
        <CodeExample endpoint={processedEndpoint} isDarkMode={isDarkMode} />
      </div>

      {/* Response Examples */}
      {endpoint.responses && (
        <ResponseExample responses={endpoint.responses} isDarkMode={isDarkMode} />
      )}
    </div>
  );
};

export default EndpointCard;
